import { createTheme } from "@mui/material/styles";
import { themeTokens } from "./tokens";

const readCssToken = (name, fallback) => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof window.getComputedStyle !== "function" ||
    !document.documentElement
  ) {
    return fallback;
  }

  const value = window.getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  return value || fallback;
};

const toPx = (value) => `${value}px`;

const getModeFallbacks = (mode) => {
  const isDark = mode === "dark";

  if (isDark) {
    return {
      actionPrimary: "#52b8d8",
      actionPrimaryHover: "#74cae5",
      actionPrimarySoft: "rgba(82, 184, 216, 0.24)",
      actionSuccess: "#69cf8d",
      actionSuccessHover: "#8bddaa",
      actionSuccessSoft: "rgba(105, 207, 141, 0.22)",
      actionDanger: "#ff6e87",
      actionDangerHover: "#ff95a7",
      actionDangerSoft: "rgba(255, 110, 135, 0.22)",
      actionWarning: "#f7c35f",
      actionWarningHover: "#f8d07b",
      actionWarningSoft: "rgba(247, 195, 95, 0.22)",
      actionInfo: "#52b8d8",
      textPrimary: themeTokens.color.text.inversePrimary,
      textSecondary: themeTokens.color.text.inverseSecondary,
      textOnPrimary: "#0f172a",
      bgPage: themeTokens.color.surface.darkPage,
      bgSubtle: themeTokens.color.surface.darkSubtle,
      bgHighlight: themeTokens.color.surface.darkHighlight,
      bgCard: themeTokens.color.surface.darkCard,
      borderSubtle: "rgba(255, 255, 255, 0.22)",
      disabled: "rgba(255, 255, 255, 0.4)",
      disabledBackground: "rgba(255, 255, 255, 0.16)",
    };
  }

  return {
    actionPrimary: themeTokens.color.brand[500],
    actionPrimaryHover: themeTokens.color.brand[700],
    actionPrimarySoft: themeTokens.color.brand[50],
    actionSuccess: themeTokens.color.success[500],
    actionSuccessHover: themeTokens.color.success[700],
    actionSuccessSoft: themeTokens.color.success[50],
    actionDanger: themeTokens.color.danger[500],
    actionDangerHover: themeTokens.color.danger[700],
    actionDangerSoft: themeTokens.color.danger[50],
    actionWarning: themeTokens.color.warning[500],
    actionWarningHover: themeTokens.color.warning[700],
    actionWarningSoft: themeTokens.color.warning[50],
    actionInfo: themeTokens.color.info[500],
    textPrimary: themeTokens.color.text.primary,
    textSecondary: themeTokens.color.text.secondary,
    textOnPrimary: themeTokens.color.common.white,
    bgPage: themeTokens.color.surface.page,
    bgSubtle: themeTokens.color.surface.subtle,
    bgHighlight: themeTokens.color.surface.highlight,
    bgCard: themeTokens.color.surface.card,
    borderSubtle: "rgba(0, 0, 0, 0.12)",
    disabled: "rgba(0, 0, 0, 0.38)",
    disabledBackground: "rgba(0, 0, 0, 0.12)",
  };
};

export const createAppTheme = (mode = "light") => {
  const fallback = getModeFallbacks(mode);

  const resolved = {
    actionPrimary: readCssToken("color-action-primary", fallback.actionPrimary),
    actionPrimaryHover: readCssToken("color-action-primary-hover", fallback.actionPrimaryHover),
    actionPrimarySoft: readCssToken("color-action-primary-soft", fallback.actionPrimarySoft),
    actionSuccess: readCssToken("color-action-success", fallback.actionSuccess),
    actionSuccessHover: readCssToken("color-action-success-hover", fallback.actionSuccessHover),
    actionSuccessSoft: readCssToken("color-action-success-soft", fallback.actionSuccessSoft),
    actionDanger: readCssToken("color-action-danger", fallback.actionDanger),
    actionDangerHover: readCssToken("color-action-danger-hover", fallback.actionDangerHover),
    actionDangerSoft: readCssToken("color-action-danger-soft", fallback.actionDangerSoft),
    actionWarning: readCssToken("color-action-warning", fallback.actionWarning),
    actionWarningHover: readCssToken("color-action-warning-hover", fallback.actionWarningHover),
    actionWarningSoft: readCssToken("color-action-warning-soft", fallback.actionWarningSoft),
    actionInfo: readCssToken("color-action-info", fallback.actionInfo),
    textPrimary: readCssToken("color-text-primary", fallback.textPrimary),
    textSecondary: readCssToken("color-text-secondary", fallback.textSecondary),
    textOnPrimary: readCssToken("color-text-on-primary", fallback.textOnPrimary),
    bgPage: readCssToken("color-bg-page", fallback.bgPage),
    bgSubtle: readCssToken("color-bg-subtle", fallback.bgSubtle),
    bgHighlight: readCssToken("color-bg-highlight", fallback.bgHighlight),
    bgCard: readCssToken("color-bg-card", fallback.bgCard),
    borderSubtle: readCssToken("color-border-subtle", fallback.borderSubtle),
    fontFamilyBase: readCssToken("font-family-base", themeTokens.font.familyBase),
    radiusPill: readCssToken("radius-pill", toPx(themeTokens.radius.pill)),
    radiusSmall: readCssToken("radius-sm", toPx(themeTokens.radius.sm)),
    space2: readCssToken("space-2", toPx(themeTokens.spacing[2])),
  };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: resolved.actionPrimary,
        dark: resolved.actionPrimaryHover,
        light: resolved.actionPrimarySoft,
        contrastText: resolved.textOnPrimary,
      },
      secondary: {
        main: resolved.actionSuccess,
        dark: resolved.actionSuccessHover,
        light: resolved.actionSuccessSoft,
        contrastText: resolved.textOnPrimary,
      },
      error: {
        main: resolved.actionDanger,
        dark: resolved.actionDangerHover,
        light: resolved.actionDangerSoft,
        contrastText: resolved.textOnPrimary,
      },
      warning: {
        main: resolved.actionWarning,
        dark: resolved.actionWarningHover,
        light: resolved.actionWarningSoft,
        contrastText: resolved.textOnPrimary,
      },
      info: {
        main: resolved.actionInfo,
        contrastText: resolved.textOnPrimary,
      },
      success: {
        main: resolved.actionSuccess,
        dark: resolved.actionSuccessHover,
        light: resolved.actionSuccessSoft,
        contrastText: resolved.textOnPrimary,
      },
      text: {
        primary: resolved.textPrimary,
        secondary: resolved.textSecondary,
      },
      background: {
        default: resolved.bgPage,
        paper: resolved.bgCard,
      },
      divider: resolved.borderSubtle,
      action: {
        disabled: fallback.disabled,
        disabledBackground: fallback.disabledBackground,
      },
    },
    typography: {
      fontFamily: resolved.fontFamilyBase,
      fontSize: themeTokens.font.sizeBase,
      h1: {
        fontSize: "3rem",
        fontFamily: resolved.fontFamilyBase,
        fontWeight: themeTokens.font.weightSemibold,
      },
      h2: {
        fontSize: "2.125rem",
        fontFamily: resolved.fontFamilyBase,
        fontWeight: themeTokens.font.weightSemibold,
      },
      h3: {
        fontSize: "1.5rem",
        fontFamily: resolved.fontFamilyBase,
        fontWeight: themeTokens.font.weightSemibold,
      },
      h4: {
        fontSize: "1.25rem",
        fontFamily: resolved.fontFamilyBase,
        fontWeight: themeTokens.font.weightBold,
      },
      h5: {
        fontSize: "1.25rem",
        fontFamily: resolved.fontFamilyBase,
        textTransform: "uppercase",
      },
      h6: {
        fontSize: "0.75rem",
        fontFamily: resolved.fontFamilyBase,
        fontWeight: themeTokens.font.weightSemibold,
        textTransform: "uppercase",
      },
      body1: {
        fontFamily: resolved.fontFamilyBase,
      },
      body2: {
        fontFamily: resolved.fontFamilyBase,
      },
      subtitle1: {
        fontFamily: resolved.fontFamilyBase,
      },
      subtitle2: {
        fontFamily: resolved.fontFamilyBase,
        fontWeight: themeTokens.font.weightSemibold,
      },
      overline: {
        fontFamily: resolved.fontFamilyBase,
      },
      caption: {
        fontFamily: resolved.fontFamilyBase,
      },
    },
    shape: {
      borderRadius: themeTokens.radius.pill,
      borderRadiusSmall: themeTokens.radius.sm,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: resolved.bgPage,
            color: resolved.textPrimary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: resolved.bgCard,
            backgroundImage: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: resolved.bgCard,
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: themeTokens.font.weightSemibold,
            borderRadius: resolved.radiusSmall,
            minHeight: 36,
            padding: "8px 16px",
            lineHeight: 1.2,
            boxShadow: "none",
            borderWidth: 1,
          },
          sizeSmall: {
            minHeight: 32,
            padding: "6px 12px",
            fontSize: "12px",
          },
          containedPrimary: {
            backgroundColor: resolved.actionPrimary,
            color: resolved.textOnPrimary,
            "&:hover": {
              backgroundColor: resolved.actionPrimaryHover,
              boxShadow: "none",
            },
          },
          outlinedInherit: {
            borderColor: "transparent",
            backgroundColor: resolved.bgSubtle,
            color: resolved.textPrimary,
            "&:hover": {
              borderColor: "transparent",
              backgroundColor: resolved.actionPrimarySoft,
              color: resolved.actionPrimary,
            },
          },
          outlinedPrimary: {
            borderColor: "transparent",
            backgroundColor: resolved.bgSubtle,
            color: resolved.textPrimary,
            "&:hover": {
              borderColor: "transparent",
              backgroundColor: resolved.actionPrimarySoft,
              color: resolved.actionPrimary,
            },
          },
          outlinedError: {
            borderColor: "transparent",
            backgroundColor: resolved.actionDangerSoft,
            color: resolved.actionDanger,
            "&:hover": {
              borderColor: "transparent",
              backgroundColor: resolved.actionDangerSoft,
            },
          },
          textInherit: {
            color: resolved.textSecondary,
            "&:hover": {
              backgroundColor: resolved.bgHighlight,
            },
          },
          textPrimary: {
            color: resolved.textSecondary,
            "&:hover": {
              backgroundColor: resolved.bgHighlight,
            },
          },
          textError: {
            color: resolved.actionDanger,
            "&:hover": {
              backgroundColor: resolved.actionDangerSoft,
            },
          },
          containedError: {
            backgroundColor: resolved.actionDanger,
            color: resolved.textOnPrimary,
            "&:hover": {
              backgroundColor: resolved.actionDangerHover,
              boxShadow: "none",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: resolved.radiusSmall,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: resolved.radiusSmall,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: resolved.radiusSmall,
          },
        },
      },
    },
  });
};

const theme = createAppTheme("light");

export default theme;
